const MAX_VIDEO_SIZE_BYTES = 20 * 1024 * 1024;
const TARGET_VIDEO_SIZE_BYTES = 19 * 1024 * 1024;
const AUDIO_BITRATE_KBPS = 96;

let ffmpegPromise;
let ffmpegScriptPromise;
let watermarkPromise;

const loadFFmpegScript = (scriptUrl) => {
  if (window.FFmpegWASM?.FFmpeg) return Promise.resolve();
  if (!ffmpegScriptPromise) {
    ffmpegScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Could not load the video compression engine."));
      document.head.appendChild(script);
    }).catch((error) => {
      ffmpegScriptPromise = null;
      throw error;
    });
  }
  return ffmpegScriptPromise;
};

const getFFmpeg = async () => {
  if (!ffmpegPromise) {
    ffmpegPromise = (async () => {
      const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      await loadFFmpegScript(`${publicUrl}/ffmpeg/ffmpeg.js`);
      if (!window.FFmpegWASM?.FFmpeg) {
        throw new Error("The video compression engine did not initialize.");
      }
      const ffmpeg = new window.FFmpegWASM.FFmpeg();
      await ffmpeg.load({
        coreURL: `${publicUrl}/ffmpeg-core/ffmpeg-core.js`,
        wasmURL: `${publicUrl}/ffmpeg-core/ffmpeg-core.wasm`,
      });
      return ffmpeg;
    })().catch((error) => {
      ffmpegPromise = null;
      throw error;
    });
  }
  return ffmpegPromise;
};

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load the SEEB video watermark."));
    image.src = url;
  });

const getWatermark = async () => {
  if (!watermarkPromise) {
    watermarkPromise = (async () => {
      const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      const logo = await loadImage(`${publicUrl}/watermarks/seeb-logo.png`);
      const logoWidth = 320;
      const logoHeight = Math.round(logo.height * (logoWidth / logo.width));
      const textGap = 12;
      const textHeight = 34;
      const canvas = document.createElement("canvas");
      canvas.width = logoWidth;
      canvas.height = logoHeight + textGap + textHeight;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Video watermarking is unavailable in this browser.");

      context.drawImage(logo, 0, 0, logoWidth, logoHeight);
      context.fillStyle = "rgba(255, 255, 255, 0.73)";
      context.font = "bold 28px Arial, sans-serif";
      context.textAlign = "center";
      context.textBaseline = "top";
      context.fillText("Seeb Interior App", logoWidth / 2, logoHeight + textGap);

      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (result) =>
            result ? resolve(result) : reject(new Error("Could not prepare the video watermark.")),
          "image/png"
        )
      );
      return new Uint8Array(await blob.arrayBuffer());
    })().catch((error) => {
      watermarkPromise = null;
      throw error;
    });
  }
  return new Uint8Array(await watermarkPromise);
};

const getVideoDuration = async (ffmpeg, inputName) => {
  let duration;
  const logHandler = ({ message }) => {
    const match = message.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
    if (!match) return;
    duration = Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]);
  };

  ffmpeg.on("log", logHandler);
  try {
    // FFmpeg exits after inspecting the input because no output is supplied.
    // Its media parser still emits the exact duration without relying on browser codec support.
    await ffmpeg.exec(["-hide_banner", "-i", inputName]);
  } finally {
    ffmpeg.off("log", logHandler);
  }

  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error("FFmpeg could not read this video's duration or codec.");
  }
  return duration;
};

const safeBaseName = (name) =>
  name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "portfolio-video";

export const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export const videoNeedsCompression = (file) =>
  file.type.startsWith("video/") && file.size > MAX_VIDEO_SIZE_BYTES;

export const compressPortfolioVideo = async (file, onProgress = () => {}) => {
  if (!file.type.startsWith("video/")) return file;

  const ffmpeg = await getFFmpeg();
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const extension =
    file.name
      .split(".")
      .pop()
      ?.replace(/[^a-z0-9]/gi, "") || "video";
  const inputName = `input-${token}.${extension}`;
  const outputName = `output-${token}.mp4`;
  const watermarkName = `watermark-${token}.png`;
  const requiresSizeCompression = videoNeedsCompression(file);
  const progressHandler = ({ progress }) =>
    onProgress(Math.max(0, Math.min(99, Math.round(progress * 100))));

  try {
    await ffmpeg.writeFile(inputName, new Uint8Array(await file.arrayBuffer()));
    await ffmpeg.writeFile(watermarkName, await getWatermark());
    const duration = await getVideoDuration(ffmpeg, inputName);
    ffmpeg.on("progress", progressHandler);
    const targetSizeBytes = Math.min(
      TARGET_VIDEO_SIZE_BYTES,
      Math.max(2 * 1024 * 1024, file.size * 0.95)
    );
    let videoBitrate = Math.max(
      180,
      Math.min(2500, Math.floor((targetSizeBytes * 8) / duration / 1000 - AUDIO_BITRATE_KBPS))
    );
    let output;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt > 0) await ffmpeg.deleteFile(outputName).catch(() => {});
      const preserveQualityAttempt = !requiresSizeCompression && attempt === 0;
      const videoFilter = preserveQualityAttempt
        ? "[0:v]null[video]"
        : "[0:v]scale=trunc(min(1280\\,iw)/2)*2:-2[video]";
      const videoQualityArgs = preserveQualityAttempt
        ? ["-crf", "18"]
        : [
            "-b:v",
            `${videoBitrate}k`,
            "-maxrate",
            `${Math.round(videoBitrate * 1.15)}k`,
            "-bufsize",
            `${videoBitrate * 2}k`,
          ];
      const exitCode = await ffmpeg.exec([
        "-i",
        inputName,
        "-i",
        watermarkName,
        "-filter_complex",
        `${videoFilter};[1:v][video]scale2ref=w=main_w*0.24:h=ow/mdar[watermark][base];[base][watermark]overlay=x=(main_w-overlay_w)/2:y=(main_h-overlay_h)/2[outv]`,
        "-map",
        "[outv]",
        "-map",
        "0:a?",
        "-c:v",
        "libx264",
        "-preset",
        "veryfast",
        ...videoQualityArgs,
        "-c:a",
        "aac",
        "-b:a",
        `${AUDIO_BITRATE_KBPS}k`,
        "-movflags",
        "+faststart",
        outputName,
      ]);
      if (exitCode !== 0) throw new Error("Video compression failed.");
      output = await ffmpeg.readFile(outputName);
      if (output.byteLength <= MAX_VIDEO_SIZE_BYTES) break;
      videoBitrate = Math.max(
        150,
        Math.floor(videoBitrate * (targetSizeBytes / output.byteLength))
      );
    }

    if (!output || output.byteLength > MAX_VIDEO_SIZE_BYTES) {
      throw new Error("Could not reduce this video below 20 MB. Please choose a shorter video.");
    }

    onProgress(100);
    const suffix = requiresSizeCompression ? "compressed-watermarked" : "watermarked";
    return new File([output], `${safeBaseName(file.name)}-${suffix}.mp4`, {
      type: "video/mp4",
      lastModified: Date.now(),
    });
  } finally {
    ffmpeg.off("progress", progressHandler);
    await ffmpeg.deleteFile(inputName).catch(() => {});
    await ffmpeg.deleteFile(outputName).catch(() => {});
    await ffmpeg.deleteFile(watermarkName).catch(() => {});
  }
};
